import { useCallback, useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatLongDate, todayISO, STATUS_IT } from "@/lib/format";
import { AdminButton, AdminField, adminInput } from "@/components/admin/ui";

const EMPTY = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  notes: "",
  guest_count: 2,
  source: "phone",
  status: "confirmed",
};

export default function ReservationDialog({ mode, date, reservation, onClose, onSaved }) {
  const editing = mode === "edit";
  const [form, setForm] = useState(
    editing
      ? {
          first_name: reservation.first_name,
          last_name: reservation.last_name,
          phone: reservation.phone,
          email: reservation.email,
          notes: reservation.notes || "",
          guest_count: reservation.guest_count,
          status: reservation.status,
          time: reservation.time,
        }
      : { ...EMPTY },
  );
  const [resDate, setResDate] = useState(editing ? reservation.date : date || todayISO());
  const [serviceId, setServiceId] = useState(editing ? reservation.service_id : "");
  const [time, setTime] = useState(editing ? reservation.time : "");
  const [avail, setAvail] = useState(null);
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadAvail = useCallback(async (d, g) => {
    try {
      const { data } = await api.get("/availability", { params: { date: d, guests: g } });
      setAvail(data);
      const services = data.services || [];
      setServiceId((current) => {
        if (services.some((s) => s.id === current)) return current;
        return services.length === 1 ? services[0].id : "";
      });
    } catch (err) {
      setAvail({ closed: true, reason: apiError(err) });
    }
  }, []);

  useEffect(() => {
    if (!editing) loadAvail(resDate, form.guest_count);
  }, [editing, resDate, form.guest_count, loadAvail]);

  const services = avail?.services || [];

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (!editing && (!serviceId || !time)) {
      setError("Seleziona servizio e orario.");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/reservations/${reservation.id}`, {
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          notes: form.notes,
          guest_count: Number(form.guest_count),
          status: form.status,
          time: form.time,
        });
      } else {
        await api.post("/admin/reservations", {
          date: resDate,
          service_id: serviceId,
          time,
          guest_count: Number(form.guest_count),
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          email: form.email,
          notes: form.notes,
          source: form.source,
          status: form.status,
          override_capacity: override,
          privacy_accepted: true,
        });
      }
      onSaved();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-wood-deep/70 flex items-start md:items-center justify-center overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      aria-label={editing ? "Modifica prenotazione" : "Nuova prenotazione"}
    >
      <form
        onSubmit={save}
        data-testid="reservation-dialog"
        className="w-full max-w-2xl bg-cream border border-wood/20 my-8"
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="font-serif text-2xl text-wood">
            {editing ? "Modifica prenotazione" : "Nuova prenotazione"}
          </h2>
          <button type="button" onClick={onClose} aria-label="Chiudi" data-testid="dialog-close" className="p-1 text-wood/60">
            <X size={20} />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {!editing && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <AdminField label="Data">
                  <input
                    type="date"
                    min={todayISO()}
                    value={resDate}
                    onChange={(e) => e.target.value && setResDate(e.target.value)}
                    className={adminInput}
                    data-testid="dialog-date"
                  />
                </AdminField>
                <AdminField label="Persone">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={form.guest_count}
                    onChange={(e) => setForm({ ...form, guest_count: e.target.value })}
                    className={adminInput}
                    data-testid="dialog-guests"
                  />
                </AdminField>
              </div>

              {avail?.closed ? (
                <p className="border border-gold/50 bg-gold/10 px-4 py-3 text-sm text-wood">
                  {avail.reason || "Chiuso in questa data."}
                </p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <AdminField label="Servizio">
                    <select
                      value={serviceId}
                      onChange={(e) => {
                        setServiceId(e.target.value);
                        setTime("");
                      }}
                      className={adminInput}
                      data-testid="dialog-service"
                    >
                      <option value="">Seleziona…</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.start_time}–{s.end_time}) · {s.booked}/{s.capacity}
                        </option>
                      ))}
                    </select>
                  </AdminField>
                  <AdminField label="Orario">
                    <select
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className={adminInput}
                      data-testid="dialog-time"
                    >
                      <option value="">Seleziona…</option>
                      {(services.find((s) => s.id === serviceId)?.slots || []).map((s) => (
                        <option key={s.time} value={s.time}>
                          {s.time} {s.available ? `· ${s.remaining} liberi` : "· completo"}
                        </option>
                      ))}
                    </select>
                  </AdminField>
                </div>
              )}
            </>
          )}

          {editing && (
            <div className="grid gap-4 sm:grid-cols-3">
              <AdminField label="Data">
                <input value={formatLongDate(reservation.date)} className={adminInput} disabled />
              </AdminField>
              <AdminField label="Orario">
                <input
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  className={adminInput}
                  data-testid="dialog-edit-time"
                  placeholder="20:30"
                />
              </AdminField>
              <AdminField label="Persone">
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={form.guest_count}
                  onChange={(e) => setForm({ ...form, guest_count: e.target.value })}
                  className={adminInput}
                  data-testid="dialog-edit-guests"
                />
              </AdminField>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <AdminField label="Nome">
              <input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className={adminInput}
                data-testid="dialog-first-name"
                required
              />
            </AdminField>
            <AdminField label="Cognome">
              <input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className={adminInput}
                data-testid="dialog-last-name"
                required
              />
            </AdminField>
            <AdminField label="Telefono">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={adminInput}
                data-testid="dialog-phone"
                required
              />
            </AdminField>
            {!editing && (
              <AdminField label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={adminInput}
                  data-testid="dialog-email"
                  required
                />
              </AdminField>
            )}
            <AdminField label="Stato">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={adminInput}
                data-testid="dialog-status"
              >
                {["pending", "confirmed", "completed", "cancelled", "no-show"].map((s) => (
                  <option key={s} value={s}>
                    {STATUS_IT[s] || s}
                  </option>
                ))}
              </select>
            </AdminField>
            {!editing && (
              <AdminField label="Origine">
                <select
                  value={form.source}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                  className={adminInput}
                  data-testid="dialog-source"
                >
                  {["phone", "walk-in", "admin", "online"].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </AdminField>
            )}
            <AdminField label="Note" className="sm:col-span-2">
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className={adminInput}
                data-testid="dialog-notes"
              />
            </AdminField>
          </div>

          {!editing && (
            <label className="flex items-start gap-3 text-sm text-foreground/70">
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
                data-testid="dialog-override"
                className="mt-1 h-4 w-4 accent-[hsl(var(--sage))]"
              />
              <span>
                Forza la capienza. <strong className="text-destructive">Attenzione:</strong> supera i limiti
                configurati e può generare overbooking.
              </span>
            </label>
          )}

          {error && (
            <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive" role="alert" data-testid="dialog-error">
              {error}
            </p>
          )}
        </div>

        <footer className="flex justify-end gap-3 border-t border-border px-5 py-4">
          <AdminButton variant="ghost" onClick={onClose}>
            Annulla
          </AdminButton>
          <button
            type="submit"
            disabled={busy}
            data-testid="dialog-save"
            className="inline-flex items-center gap-2 bg-sage px-6 py-2.5 text-[0.65rem] uppercase tracking-[0.16em] text-cream hover:bg-wood transition-colors duration-400 disabled:opacity-50"
          >
            {busy && <Loader2 size={13} className="animate-spin" />} Salva
          </button>
        </footer>
      </form>
    </div>
  );
}
